import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Settings2,
  IndianRupee,
  Ban,
  RotateCcw,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowUpRight,
  ExternalLink,
  Filter,
  Search,
  X,
  Building2,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DATE_PRESETS = [
  { label: 'All Time', value: 'all' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Previous Month', value: 'prev_month' },
  { label: 'This Quarter', value: 'this_quarter' },
  { label: 'Previous Quarter', value: 'prev_quarter' },
  { label: 'This Year', value: 'this_year' },
  { label: 'Custom Range', value: 'custom' },
];

const STATUS_OPTIONS = [
  { label: 'Paid', value: 'paid' },
  { label: 'Sent', value: 'sent' },
  { label: 'Partially Paid', value: 'partially_paid' },
  { label: 'Overdue', value: 'overdue' },
  { label: 'Draft', value: 'draft' },
  { label: 'Void', value: 'void' },
];

function getDateRange(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case 'this_month': return { from: new Date(y, m, 1), to: new Date(y, m + 1, 0) };
    case 'prev_month': return { from: new Date(y, m - 1, 1), to: new Date(y, m, 0) };
    case 'this_quarter': { const q = Math.floor(m / 3) * 3; return { from: new Date(y, q, 1), to: new Date(y, q + 3, 0) }; }
    case 'prev_quarter': { const q = Math.floor(m / 3) * 3 - 3; return { from: new Date(y, q, 1), to: new Date(y, q + 3, 0) }; }
    case 'this_year': return { from: new Date(y, 0, 1), to: new Date(y, 11, 31) };
    default: return null;
  }
}

function toYMD(d) { return d.toISOString().split('T')[0]; }

const InvoicesPage = () => {
  const { getAuthHeaders } = useAuth();
  const { selectedFY, fyOptions } = useFinancialYear();

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [settings, setSettings] = useState({ from_date: '2026-04-01', status_filter: 'paid', last_synced: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ from_date: '', status_filter: '' });

  // Exclude popover
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [excludeInvoice, setExcludeInvoice] = useState(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [excludeLoading, setExcludeLoading] = useState(false);

  // Move to FY
  const [fyDialogOpen, setFyDialogOpen] = useState(false);
  const [fyInvoice, setFyInvoice] = useState(null);
  const [fyTarget, setFyTarget] = useState('');
  const [fySaving, setFySaving] = useState(false);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'Corporate Training', 'Venue Services'
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedFY) params.append('financial_year', selectedFY);
      const resp = await axios.get(`${API}/zoho/invoices?${params}`, { headers: getAuthHeaders() });
      setInvoices(resp.data.invoices || []);
      setSummary(resp.data.summary || {});
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedFY]);

  const fetchSettings = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/zoho/settings`, { headers: getAuthHeaders() });
      setSettings(resp.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch settings:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchInvoices(); fetchSettings(); }, [fetchInvoices, fetchSettings]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncProgress('Starting...');
    try {
      const resp = await axios.post(`${API}/zoho/sync`, {}, { headers: getAuthHeaders() });
      const jobId = resp.data?.job_id;
      if (!jobId) {
        // Legacy synchronous response — show message and refresh
        toast.success(resp.data?.message || 'Sync completed');
        fetchInvoices(); fetchSettings();
        return;
      }
      toast.info('Sync started in background. Polling for progress...');
      const start = Date.now();
      const MAX_MS = 5 * 60 * 1000;
      while (Date.now() - start < MAX_MS) {
        await new Promise(r => setTimeout(r, 3000));
        setSyncProgress(`Syncing... (${Math.round((Date.now() - start) / 1000)}s)`);
        const poll = await axios.get(`${API}/zoho/sync-jobs/${jobId}`, { headers: getAuthHeaders() });
        if (poll.data?.status === 'success') {
          toast.success(poll.data.result?.message || 'Sync completed');
          fetchInvoices(); fetchSettings();
          return;
        }
        if (poll.data?.status === 'failed') {
          toast.error(poll.data?.error || 'Sync failed');
          return;
        }
      }
      toast.error('Sync timed out after 5 minutes — check Recent Sync Jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Sync failed');
    } finally {
      setSyncing(false);
      setSyncProgress('');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.post(`${API}/zoho/settings`, settingsForm, { headers: getAuthHeaders() });
      toast.success('Settings updated');
      setSettingsOpen(false); fetchSettings();
    } catch (error) { toast.error('Failed to save settings'); }
  };

  const handleExclude = async () => {
    if (!excludeInvoice) return;
    try {
      setExcludeLoading(true);
      await axios.post(`${API}/zoho/invoices/${excludeInvoice.invoice_id}/exclude`, { reason: excludeReason }, { headers: getAuthHeaders() });
      toast.success('Invoice excluded from FY');
      setExcludeDialogOpen(false); setExcludeInvoice(null); setExcludeReason(''); fetchInvoices();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to exclude'); } finally { setExcludeLoading(false); }
  };

  const handleInclude = async (invoiceId) => {
    try {
      await axios.post(`${API}/zoho/invoices/${invoiceId}/include`, {}, { headers: getAuthHeaders() });
      toast.success('Invoice re-included in FY'); fetchInvoices();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to include'); }
  };

  const openFyMove = (inv) => {
    setFyInvoice(inv);
    setFyTarget(inv.manual_fy || selectedFY || '');
    setFyDialogOpen(true);
  };

  const handleFyMove = async (clear) => {
    if (!fyInvoice) return;
    setFySaving(true);
    try {
      await axios.put(`${API}/zoho/invoices/${fyInvoice.invoice_id}/financial-year`,
        { manual_fy: clear ? null : fyTarget },
        { headers: getAuthHeaders() });
      toast.success(clear ? 'FY override cleared' : `Invoice moved to FY ${fyTarget}`);
      setFyDialogOpen(false); setFyInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update FY');
    } finally {
      setFySaving(false);
    }
  };

  const toggleStatus = (status) => {
    setSelectedStatuses(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  const clearFilters = () => {
    setCategoryFilter('all'); setSelectedStatuses([]); setDatePreset('all'); setCustomFrom(''); setCustomTo(''); setCustomerSearch(''); setProjectSearch('');
  };

  // Apply filters
  const filteredInvoices = useMemo(() => {
    let result = [...invoices];

    // Category filter
    if (categoryFilter !== 'all') {
      result = result.filter(i => (i.branch_name || i.category || '') === categoryFilter);
    }

    // Status multi-select
    if (selectedStatuses.length > 0) {
      result = result.filter(i => selectedStatuses.includes(i.status) || (i.excluded && selectedStatuses.includes('excluded')));
    }

    // Date filter
    if (datePreset !== 'all') {
      let range;
      if (datePreset === 'custom' && customFrom && customTo) {
        range = { from: new Date(customFrom), to: new Date(customTo) };
      } else {
        range = getDateRange(datePreset);
      }
      if (range) {
        result = result.filter(i => {
          const d = new Date(i.date);
          return d >= range.from && d <= range.to;
        });
      }
    }

    // Customer search
    if (customerSearch.trim()) {
      const q = customerSearch.toLowerCase();
      result = result.filter(i => (i.customer_name || '').toLowerCase().includes(q));
    }

    // Project ID search
    if (projectSearch.trim()) {
      result = result.filter(i => (i.crm_project_id || '').includes(projectSearch.trim()));
    }

    return result;
  }, [invoices, categoryFilter, selectedStatuses, datePreset, customFrom, customTo, customerSearch, projectSearch]);

  const includedFiltered = filteredInvoices.filter(i => !i.excluded);
  const excludedFiltered = filteredInvoices.filter(i => i.excluded);
  const activeFilterCount = [categoryFilter !== 'all', selectedStatuses.length > 0, datePreset !== 'all', customerSearch.trim(), projectSearch.trim()].filter(Boolean).length;

  // Compute summary from filtered data so cards reflect active filters
  const filteredSummary = useMemo(() => {
    const included = filteredInvoices.filter(i => !i.excluded);
    const paidIncluded = included.filter(i => i.status === 'paid' || i.status === 'partially_paid');
    const unpaidIncluded = included.filter(i => i.status !== 'paid' && i.status !== 'partially_paid');
    const excluded = filteredInvoices.filter(i => i.excluded);
    const toINR = (inv, field) => {
      const val = inv[field] || 0;
      if (inv.currency_code && inv.currency_code !== 'INR' && inv.exchange_rate) return val * inv.exchange_rate;
      return val;
    };
    return {
      total_invoices: filteredInvoices.length,
      total_billed: included.reduce((s, i) => s + toINR(i, 'total'), 0),
      total_net_received: paidIncluded.reduce((s, i) => s + (i.net_amount || 0), 0),
      total_awaiting: unpaidIncluded.reduce((s, i) => s + toINR(i, 'total'), 0),
      excluded_count: excluded.length,
      unpaid_count: unpaidIncluded.length,
    };
  }, [filteredInvoices]);

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  const formatOriginalCurrency = (amount, currencyCode, symbol) => {
    if (!currencyCode || currencyCode === 'INR') return formatCurrency(amount);
    return `${symbol || currencyCode + ' '}${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(amount || 0)}`;
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate().toString().padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
  };

  const getStatusBadge = (status, excluded) => {
    if (excluded) return <Badge className="bg-slate-200 text-slate-500 text-xs">EXCLUDED</Badge>;
    const map = {
      paid: { color: 'bg-emerald-100 text-emerald-700', label: 'PAID', icon: CheckCircle2 },
      partially_paid: { color: 'bg-blue-100 text-blue-700', label: 'PARTIAL', icon: Clock },
      sent: { color: 'bg-amber-100 text-amber-700', label: 'SENT', icon: ArrowUpRight },
      overdue: { color: 'bg-red-100 text-red-700', label: 'OVERDUE', icon: AlertCircle },
      draft: { color: 'bg-slate-100 text-slate-600', label: 'DRAFT', icon: FileText },
      void: { color: 'bg-slate-100 text-slate-500', label: 'VOID', icon: Ban }
    };
    const cfg = map[status] || { color: 'bg-slate-100 text-slate-600', label: status?.toUpperCase() || '-', icon: FileText };
    const Icon = cfg.icon;
    return <Badge className={`${cfg.color} font-mono text-xs inline-flex items-center gap-1`}><Icon className="w-3 h-3" />{cfg.label}</Badge>;
  };

  const renderInvoiceRow = (inv, isExcluded = false) => (
    <React.Fragment key={inv.invoice_id}>
      <tr
        className={`border-b border-slate-100 cursor-pointer transition-colors ${
          isExcluded ? 'opacity-50 hover:opacity-75' :
          expandedRow === inv.invoice_id ? 'bg-indigo-50' : 'hover:bg-slate-50'
        }`}
        onClick={() => setExpandedRow(expandedRow === inv.invoice_id ? null : inv.invoice_id)}
        data-testid={`invoice-row-${inv.invoice_id}`}
      >
        <td className="p-3 text-center">
          {expandedRow === inv.invoice_id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </td>
        <td className={`p-3 font-mono text-indigo-600 font-semibold text-xs ${isExcluded ? 'line-through text-slate-400' : ''}`}>{inv.invoice_number}</td>
        <td className={`p-3 ${isExcluded ? 'line-through text-slate-400' : ''}`}>{inv.customer_name}</td>
        <td className="p-3 text-slate-500">{formatDate(inv.date)}</td>
        <td className="p-3 text-xs">{inv.branch_name || inv.category || '-'}</td>
        <td className="p-3 text-xs">{inv.branch_name || '-'}</td>
        <td className="p-3 font-mono text-xs text-slate-500">{inv.crm_project_id || '-'}</td>
        <td className="p-3 text-right font-mono">
          {inv.currency_code && inv.currency_code !== 'INR' ? (
            <div>
              <span>{formatOriginalCurrency(inv.total, inv.currency_code, inv.currency_symbol)}</span>
              {inv.exchange_rate > 0 && <div className="text-xs text-slate-400">{formatCurrency(inv.total * inv.exchange_rate)}</div>}
            </div>
          ) : formatCurrency(inv.total)}
        </td>
        <td className="p-3 text-right font-mono text-emerald-600">
          {!isExcluded && (inv.status === 'paid' || inv.status === 'partially_paid') ? formatCurrency(inv.net_amount) : '-'}
        </td>
        <td className="p-3 text-center">{getStatusBadge(inv.status, isExcluded)}</td>
        <td className="p-3 text-center">
          {settings?.books_base_url && (
            <a
              href={`${settings.books_base_url}#/invoices/${inv.invoice_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center justify-center w-7 h-7 rounded text-indigo-600 hover:bg-indigo-100"
              title="Open in Zoho Books"
              data-testid={`inv-zoho-link-${inv.invoice_id}`}
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </td>
      </tr>

      {expandedRow === inv.invoice_id && (
        <tr>
          <td colSpan="11" className="p-0">
            <div className="bg-slate-50 border-t border-b border-slate-200 p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-indigo-950">Invoice Details</h3>
                  <p className="text-xs text-slate-400 mt-1">{inv.customer_name}</p>
                </div>
                <div className="flex gap-2 items-center">
                  {settings?.books_base_url && (
                    <a
                      href={`${settings.books_base_url}#/invoices/${inv.invoice_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                      data-testid={`inv-zoho-link-detail-${inv.invoice_id}`}
                    >
                      <ExternalLink className="w-3 h-3" />Open in Zoho
                    </a>
                  )}
                  {isExcluded ? (
                    <Button variant="outline" size="sm" className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      onClick={(e) => { e.stopPropagation(); handleInclude(inv.invoice_id); }}
                      data-testid={`include-btn-${inv.invoice_id}`}
                    ><RotateCcw className="w-3 h-3 mr-1" />Re-include in FY</Button>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        onClick={(e) => { e.stopPropagation(); openFyMove(inv); }}
                        data-testid={`fy-move-btn-${inv.invoice_id}`}
                      ><ArrowUpRight className="w-3 h-3 mr-1" />
                        {inv.manual_fy ? `FY: ${inv.manual_fy}` : 'Move to FY'}
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); setExcludeInvoice(inv); setExcludeReason(''); setExcludeDialogOpen(true); }}
                        data-testid={`exclude-btn-${inv.invoice_id}`}
                      ><Ban className="w-3 h-3 mr-1" />Exclude from FY</Button>
                    </>
                  )}
                </div>
              </div>

              {isExcluded && inv.exclude_reason && (
                <p className="text-sm text-red-500">Excluded reason: {inv.exclude_reason}</p>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><span className="text-slate-500">Invoice #:</span> <span className="font-mono font-medium">{inv.invoice_number}</span></div>
                <div><span className="text-slate-500">Date:</span> {formatDate(inv.date)}</div>
                <div><span className="text-slate-500">CRM Project:</span> <span className="font-mono">{inv.crm_project_id || '-'}</span></div>
                <div><span className="text-slate-500">Category:</span> {inv.category || '-'}</div>
                <div><span className="text-slate-500">Branch:</span> {inv.branch_name || '-'}</div>
                <div><span className="text-slate-500">Reference:</span> {inv.reference_no || '-'}</div>
                <div><span className="text-slate-500">Program:</span> {inv.program_name || '-'}</div>
                <div><span className="text-slate-500">Client Type:</span> {inv.client_type || '-'}</div>
              </div>

              {inv.custom_fields && Object.keys(inv.custom_fields).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Custom Fields</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs bg-white rounded-lg border border-slate-200 p-3">
                    {Object.entries(inv.custom_fields).filter(([, v]) => v && String(v) !== '0' && String(v) !== '0.0' && String(v) !== 'False').map(([key, val]) => (
                      <div key={key}><span className="text-slate-400">{key}:</span> <span className="text-slate-700 font-medium">{String(val)}</span></div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-lg border border-slate-200 p-4 max-w-sm">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Amount Breakdown</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Sub Total</span><span className="font-mono">{formatOriginalCurrency(inv.sub_total, inv.currency_code, inv.currency_symbol)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">GST</span><span className="font-mono">{formatOriginalCurrency(inv.tax_total, inv.currency_code, inv.currency_symbol)}</span></div>
                  <div className="flex justify-between border-t pt-1 font-semibold"><span>Invoice Total</span><span className="font-mono">{formatOriginalCurrency(inv.total, inv.currency_code, inv.currency_symbol)}</span></div>
                  {inv.currency_code && inv.currency_code !== 'INR' && inv.exchange_rate > 0 && (
                    <>
                      <div className="flex justify-between text-xs pt-1"><span className="text-blue-500">Exchange Rate</span><span className="font-mono text-blue-600">1 {inv.currency_code} = {formatCurrency(inv.exchange_rate)}</span></div>
                      <div className="flex justify-between font-semibold text-indigo-700"><span>Total (INR)</span><span className="font-mono">{formatCurrency(inv.total * inv.exchange_rate)}</span></div>
                    </>
                  )}
                </div>
              </div>

              {inv.payments && inv.payments.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Payment Details</h4>
                  <table className="w-full text-sm bg-white rounded-lg border border-slate-200">
                    <thead><tr className="bg-slate-50 border-b">
                      <th className="text-left p-2 text-xs font-semibold text-slate-500">Payment #</th>
                      <th className="text-left p-2 text-xs font-semibold text-slate-500">Date</th>
                      <th className="text-left p-2 text-xs font-semibold text-slate-500">Mode</th>
                      <th className="text-right p-2 text-xs font-semibold text-slate-500">Amount</th>
                      <th className="text-right p-2 text-xs font-semibold text-slate-500">TDS</th>
                    </tr></thead>
                    <tbody>
                      {inv.payments.map((p, pIdx) => (
                        <tr key={p.payment_id || `pay-${pIdx}`} className="border-b border-slate-100">
                          <td className="p-2 font-mono text-xs">{p.payment_number}</td>
                          <td className="p-2">{formatDate(p.date)}</td>
                          <td className="p-2 text-xs">{p.payment_mode}</td>
                          <td className="p-2 text-right font-mono">
                            {inv.currency_code && inv.currency_code !== 'INR' ? (
                              <div>
                                <span>{formatOriginalCurrency(p.amount, inv.currency_code, inv.currency_symbol)}</span>
                                {(p.amount_inr || (inv.exchange_rate && inv.exchange_rate > 0)) && (
                                  <div className="text-xs text-slate-400">{formatCurrency(p.amount_inr || p.amount * inv.exchange_rate)}</div>
                                )}
                              </div>
                            ) : formatCurrency(p.amount)}
                          </td>
                          <td className="p-2 text-right font-mono text-red-600">{p.tds_withheld ? formatCurrency(p.tds_withheld) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      <IndianRupee className="w-4 h-4 text-emerald-600" />
                      <span className="font-medium text-emerald-800">
                        {inv.currency_code && inv.currency_code !== 'INR' && inv.exchange_rate > 0 ? (
                          <>Net (INR) = ({formatOriginalCurrency(inv.total, inv.currency_code, inv.currency_symbol)} - {formatOriginalCurrency(inv.tax_total, inv.currency_code, inv.currency_symbol)}) x {inv.exchange_rate.toFixed(2)} - {formatCurrency(inv.total_tds)} (TDS) = <span className="font-bold">{formatCurrency(inv.net_amount)}</span></>
                        ) : (
                          <>Net = {formatCurrency(inv.total)} - {formatCurrency(inv.tax_total)} (GST) - {formatCurrency(inv.total_tds)} (TDS) = <span className="font-bold">{formatCurrency(inv.net_amount)}</span></>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {(!inv.payments || inv.payments.length === 0) && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">No payments recorded yet.</div>
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-indigo-950 flex items-center gap-3">
            <FileText className="w-7 h-7" />Revenue & Invoices
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Track invoice payments and revenue from Zoho Books
            {settings.last_synced && <span className="ml-2 text-xs text-slate-400">Last synced: {formatDate(settings.last_synced?.split('T')[0])}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setSettingsForm({ from_date: settings.from_date || '', status_filter: settings.status_filter || 'paid' }); setSettingsOpen(true); }} data-testid="sync-settings-btn">
            <Settings2 className="w-4 h-4 mr-2" />Settings
          </Button>
          <Button size="sm" className="bg-indigo-600" onClick={handleSync} disabled={syncing} data-testid="sync-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />{syncing ? (syncProgress || 'Syncing...') : 'Sync from Zoho Books'}
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-400">Showing data for FY {selectedFY}</p>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-slate-200"><CardContent className="p-4"><p className="text-xs text-slate-500">Invoices</p><p className="text-2xl font-bold font-mono text-indigo-950" data-testid="invoice-count">{filteredSummary.total_invoices || 0}</p></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><p className="text-xs text-slate-500">Total Billed</p><p className="text-2xl font-bold font-mono text-indigo-950">{formatCurrency(filteredSummary.total_billed)}</p></CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4"><p className="text-xs text-emerald-600">Received (Net)</p><p className="text-2xl font-bold font-mono text-emerald-700">{formatCurrency(filteredSummary.total_net_received)}</p></CardContent></Card>
        <Card className="border-amber-200 bg-amber-50"><CardContent className="p-4"><p className="text-xs text-amber-600">Awaiting</p><p className="text-2xl font-bold font-mono text-amber-700">{formatCurrency(filteredSummary.total_awaiting)}</p><p className="text-xs text-amber-500 mt-1">{filteredSummary.unpaid_count || 0} unpaid</p></CardContent></Card>
        {filteredSummary.excluded_count > 0 && <Card className="border-slate-200"><CardContent className="p-4"><p className="text-xs text-slate-500">Excluded</p><p className="text-2xl font-bold font-mono text-slate-600">{filteredSummary.excluded_count}</p></CardContent></Card>}
      </div>

      {/* Category Toggle + Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Category toggle buttons */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="category-filter">
          <Button size="sm" variant={categoryFilter === 'all' ? 'default' : 'ghost'} className={`text-xs h-8 ${categoryFilter === 'all' ? 'bg-indigo-600 text-white' : ''}`}
            onClick={() => setCategoryFilter('all')}>All</Button>
          <Button size="sm" variant={categoryFilter === 'Corporate Training' ? 'default' : 'ghost'}
            className={`text-xs h-8 ${categoryFilter === 'Corporate Training' ? 'bg-blue-600 text-white' : ''}`}
            onClick={() => setCategoryFilter(categoryFilter === 'Corporate Training' ? 'all' : 'Corporate Training')}>
            <Building2 className="w-3 h-3 mr-1" />Corporate Training
          </Button>
          <Button size="sm" variant={categoryFilter === 'Venue Services' ? 'default' : 'ghost'}
            className={`text-xs h-8 ${categoryFilter === 'Venue Services' ? 'bg-emerald-600 text-white' : ''}`}
            onClick={() => setCategoryFilter(categoryFilter === 'Venue Services' ? 'all' : 'Venue Services')}>
            <MapPin className="w-3 h-3 mr-1" />Venue Services
          </Button>
        </div>

        {/* More Filters popover */}
        <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="text-xs h-8 relative" data-testid="more-filters-btn">
              <Filter className="w-3 h-3 mr-1" />More Filters
              {activeFilterCount > (categoryFilter !== 'all' ? 1 : 0) && (
                <span className="ml-1 bg-indigo-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                  {activeFilterCount - (categoryFilter !== 'all' ? 1 : 0)}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="start">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Filters</h4>
                <Button variant="ghost" size="sm" className="text-xs h-6 text-slate-500" onClick={clearFilters}>Clear all</Button>
              </div>

              {/* Status multi-select */}
              <div>
                <Label className="text-xs">Status</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {STATUS_OPTIONS.map(opt => (
                    <Button key={opt.value} size="sm" variant={selectedStatuses.includes(opt.value) ? 'default' : 'outline'}
                      className={`text-xs h-7 ${selectedStatuses.includes(opt.value) ? 'bg-indigo-600 text-white' : ''}`}
                      onClick={() => toggleStatus(opt.value)}
                    >{opt.label}</Button>
                  ))}
                </div>
              </div>

              {/* Date preset */}
              <div>
                <Label className="text-xs">Date Range</Label>
                <Select value={datePreset} onValueChange={setDatePreset}>
                  <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DATE_PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {datePreset === 'custom' && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-8 text-xs" />
                    <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-8 text-xs" />
                  </div>
                )}
              </div>

              {/* Customer search */}
              <div>
                <Label className="text-xs">Customer</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                  <Input value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} placeholder="Search customer..." className="pl-7 h-8 text-xs" data-testid="customer-search" />
                </div>
              </div>

              {/* Project ID search */}
              <div>
                <Label className="text-xs">CRM Project ID</Label>
                <Input value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} placeholder="e.g., 89" className="mt-1 h-8 text-xs" data-testid="project-search" />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Active filter pills */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            {selectedStatuses.map(s => (
              <Badge key={s} className="bg-indigo-100 text-indigo-700 text-xs cursor-pointer" onClick={() => toggleStatus(s)}>
                {s} <X className="w-3 h-3 ml-1" />
              </Badge>
            ))}
            {datePreset !== 'all' && (
              <Badge className="bg-indigo-100 text-indigo-700 text-xs cursor-pointer" onClick={() => setDatePreset('all')}>
                {DATE_PRESETS.find(p => p.value === datePreset)?.label} <X className="w-3 h-3 ml-1" />
              </Badge>
            )}
            {customerSearch && (
              <Badge className="bg-indigo-100 text-indigo-700 text-xs cursor-pointer" onClick={() => setCustomerSearch('')}>
                "{customerSearch}" <X className="w-3 h-3 ml-1" />
              </Badge>
            )}
            {projectSearch && (
              <Badge className="bg-indigo-100 text-indigo-700 text-xs cursor-pointer" onClick={() => setProjectSearch('')}>
                Project: {projectSearch} <X className="w-3 h-3 ml-1" />
              </Badge>
            )}
            <span className="text-xs text-slate-400 ml-1">{filteredInvoices.length} of {invoices.length}</span>
          </div>
        )}
      </div>

      {/* Invoice Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-indigo-950">Invoices</CardTitle>
          <CardDescription>Click on an invoice to view details and payment breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-14 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>{invoices.length === 0 ? 'No invoices synced yet.' : 'No invoices match your filters.'}</p>
              {invoices.length === 0 && <p className="text-sm mt-1">Click "Sync from Zoho Books" to fetch invoices.</p>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="w-8 p-3"></th>
                    <th className="text-left p-3 font-semibold text-slate-600">Invoice #</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Customer</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Date</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Category</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Branch</th>
                    <th className="text-left p-3 font-semibold text-slate-600">CRM Project ID</th>
                    <th className="text-right p-3 font-semibold text-slate-600">Total</th>
                    <th className="text-right p-3 font-semibold text-emerald-600">Net Received</th>
                    <th className="text-center p-3 font-semibold text-slate-600">Status</th>
                    <th className="text-center p-3 font-semibold text-slate-600">Zoho</th>
                  </tr>
                </thead>
                <tbody>
                  {includedFiltered.map(inv => renderInvoiceRow(inv, false))}
                  {excludedFiltered.length > 0 && (
                    <>
                      <tr><td colSpan="11" className="px-3 pt-4 pb-2"><span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Excluded from FY</span></td></tr>
                      {excludedFiltered.map(inv => renderInvoiceRow(inv, true))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Exclude Dialog */}
      <Dialog open={excludeDialogOpen} onOpenChange={setExcludeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Exclude Invoice from FY {selectedFY}?</DialogTitle>
            <DialogDescription>
              {excludeInvoice && <span>Invoice <strong>{excludeInvoice.invoice_number}</strong> ({excludeInvoice.customer_name}) will not count towards this FY's revenue.</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="exclude-reason" className="text-sm">Reason (optional)</Label>
            <Textarea id="exclude-reason" placeholder="e.g., Revenue belongs to next FY" value={excludeReason} onChange={(e) => setExcludeReason(e.target.value)} className="mt-1" rows={2} data-testid="exclude-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcludeDialogOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleExclude} disabled={excludeLoading} data-testid="confirm-exclude-btn">{excludeLoading ? 'Excluding...' : 'Exclude'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to FY Dialog */}
      <Dialog open={fyDialogOpen} onOpenChange={setFyDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="fy-move-dialog">
          <DialogHeader>
            <DialogTitle>Move Invoice to a different FY</DialogTitle>
            <DialogDescription>
              {fyInvoice && <span>Invoice <strong>{fyInvoice.invoice_number}</strong> ({fyInvoice.customer_name}) · raised on {fyInvoice.date}</span>}
              <br />
              This sets an internal FY override. The invoice will be counted only towards the chosen FY (Dashboard, P&L, Reports).
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <Label className="text-sm">Target Financial Year</Label>
            <Select value={fyTarget} onValueChange={setFyTarget}>
              <SelectTrigger className="mt-1" data-testid="fy-target-select"><SelectValue placeholder="Choose FY" /></SelectTrigger>
              <SelectContent>
                {(fyOptions || []).map(fy => (
                  <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fyInvoice?.manual_fy && (
              <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                Currently overridden to <strong>FY {fyInvoice.manual_fy}</strong>. Reset to use invoice date FY.
              </p>
            )}
          </div>
          <DialogFooter>
            {fyInvoice?.manual_fy && (
              <Button variant="outline" onClick={() => handleFyMove(true)} disabled={fySaving} data-testid="fy-clear-btn">Reset to Auto</Button>
            )}
            <Button variant="ghost" onClick={() => setFyDialogOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-950 hover:bg-indigo-900" onClick={() => handleFyMove(false)} disabled={fySaving || !fyTarget} data-testid="fy-confirm-btn">
              {fySaving ? 'Saving...' : `Move to FY ${fyTarget}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Zoho Books Sync Settings</DialogTitle><DialogDescription>Configure how invoices are fetched from Zoho Books</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-sm">Fetch Invoices From Date</Label><Input type="date" value={settingsForm.from_date} onChange={(e) => setSettingsForm({ ...settingsForm, from_date: e.target.value })} className="mt-1" data-testid="settings-from-date" /></div>
            <div>
              <Label className="text-sm">Invoice Status Filter</Label>
              <Select value={settingsForm.status_filter} onValueChange={(val) => setSettingsForm({ ...settingsForm, status_filter: val })}>
                <SelectTrigger className="mt-1" data-testid="settings-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="paid">Paid Only</SelectItem>
                  <SelectItem value="sent">Sent (Unpaid)</SelectItem>
                  <SelectItem value="partially_paid">Partially Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600" onClick={handleSaveSettings} data-testid="save-settings-btn">Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
